import React, { useState, useEffect } from 'react';
import { Control, useController, useFormContext } from 'react-hook-form';
import { Input } from '@/components/design-system/Input';
import { Button } from '@/components/design-system/Button';
import { SearchIcon, XIcon } from 'lucide-react';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { cn } from '@/utils/styles';
import { Checkbox } from '@/components/design-system/Checkbox';

interface SearchOption {
  id: string | number;
  label: string;
  value: string | number;
  searchTerms?: string[];
}

type SkipAnswer = {
  can_skip_value: boolean;
};

interface Props {
  control: Control<Record<string, unknown>>;
  name: string;
  title: string;
  subtitle?: string;
  options: SearchOption[];
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  canSkip?: boolean;
  skipLabel?: string;
  initialAnswer?: string[] | SkipAnswer;
}

const SearchFieldWrapper: React.FC<Props> = ({
  control,
  name,
  options,
  required = false,
  placeholder = 'Search and add items',
  disabled = false,
  canSkip = false,
  skipLabel,
  initialAnswer,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { setValue, getValues } = useFormContext();
  const [isSkipped, setIsSkipped] = useState(
    initialAnswer && 'can_skip_value' in initialAnswer
      ? initialAnswer.can_skip_value
      : false,
  );

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue: initialAnswer || [],
    rules: {
      validate: (value) => {
        // If not required, always valid
        if (!required) return true;

        // Check if value is a skip object
        if (
          typeof value === 'object' &&
          value !== null &&
          'can_skip_value' in value
        ) {
          return true;
        }

        // If not skipped, must have at least one selection
        if (Array.isArray(value) && value.length > 0) {
          return true;
        }

        // Otherwise invalid
        return canSkip
          ? 'Please select at least one option or use the skip option'
          : 'Please select at least one option';
      },
    },
  });

  useEffect(() => {
    if (initialAnswer) {
      // Always set the form value when skipValue is true
      setValue(name, { can_skip_value: true }, { shouldValidate: true });
    } else {
      // Only reset if the current value is the skip object
      const currentValue = getValues(name);
      if (
        currentValue &&
        typeof currentValue === 'object' &&
        currentValue.can_skip_value === true
      ) {
        setValue(name, null, { shouldValidate: true });
      }
    }
  }, [initialAnswer, name, setValue, getValues]);

  const selectedItems = (
    Array.isArray(field.value) ? field.value : []
  ) as string[];

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled || isSkipped) return;
    setSearchTerm(event.target.value);
  };

  const handleAddItem = (option: SearchOption) => {
    if (disabled || isSkipped) return;
    if (!selectedItems.includes(option.label)) {
      field.onChange([...selectedItems, option.label]);
      setSearchTerm('');
    }
  };

  const handleRemoveItem = (itemToRemove: string) => {
    if (disabled || isSkipped) return;
    const newSelectedItems = selectedItems.filter(
      (item) => item !== itemToRemove,
    );
    field.onChange(newSelectedItems);
  };

  const handleDone = () => {
    if (disabled || isSkipped) return;
    // If there's an exact match in filtered options, add it
    if (searchTerm.length > 0) {
      const exactMatch = filteredOptions.find(
        (option) => option.label.toLowerCase() === searchTerm.toLowerCase(),
      );
      if (exactMatch) {
        handleAddItem(exactMatch);
      }
    }
    setSearchTerm('');
  };

  const handleSkipChange = (checked: boolean) => {
    setIsSkipped(checked);
    if (checked) {
      field.onChange({ can_skip_value: true });
      setSearchTerm('');
    } else {
      field.onChange([]);
    }
  };

  const filteredOptions = searchTerm
    ? options.filter((option) => {
        const searchTermLower = searchTerm.toLowerCase();
        const labelMatch = option.label.toLowerCase().includes(searchTermLower);
        const searchTermsMatch = option.searchTerms?.some((term) =>
          term.toLowerCase().includes(searchTermLower),
        );
        const notSelected = !selectedItems.includes(option.label);
        return (labelMatch || searchTermsMatch) && notSelected;
      })
    : [];

  return (
    <div className="flex flex-col min-h-[400px]">
      <div className="flex-grow">
        <div className="flex items-center gap-2.5">
          <div className="relative flex-grow">
            <Input
              type="text"
              placeholder={placeholder}
              value={searchTerm}
              onChange={handleSearchChange}
              className="!pl-10 !rounded-full"
              disabled={disabled || isSkipped}
            >
              <SearchIcon
                size={20}
                className="absolute left-4 !size-5 text-tertiary"
              />
            </Input>
          </div>
          {searchTerm.length > 0 && (
            <Button
              variant="tertiary"
              onClick={handleDone}
              disabled={disabled || isSkipped}
            >
              Done
            </Button>
          )}
        </div>

        {!isSkipped && error && (
          <p className="text-red-500 mt-2">{error.message}</p>
        )}

        {!isSkipped && searchTerm.length === 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            {selectedItems.map((item, index) => (
              <div
                key={index}
                className={cn(
                  'flex items-center bg-white border-2 border-gray-600 rounded-full py-1 px-3 text-sm',
                  (disabled || isSkipped) && 'opacity-50',
                )}
              >
                {item}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveItem(item)}
                  disabled={disabled || isSkipped}
                  className="!w-6 !h-6 ml-2"
                >
                  <XIcon size={16} />
                </Button>
              </div>
            ))}
          </div>
        )}

        {!isSkipped && searchTerm.length > 0 && (
          <ul className="flex flex-col items-start list-none p-0 mt-4">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <li
                  key={option.value}
                  onClick={() => !disabled && handleAddItem(option)}
                  className={cn(
                    'flex items-center py-2 cursor-pointer text-lg',
                    (disabled || isSkipped) && 'opacity-50 cursor-not-allowed',
                  )}
                >
                  <span>{option.label}</span>
                  <span className="ml-2">
                    <PlusIcon />
                  </span>
                </li>
              ))
            ) : (
              <div className="w-full flex flex-col items-center justify-center py-8 space-y-4">
                <p className="text-center typography-body">
                  No results found. Please try a different search term.
                </p>
              </div>
            )}
          </ul>
        )}
      </div>

      {canSkip && (
        <div className="flex justify-center items-center mt-auto">
          <div className="flex items-center gap-2">
            <Checkbox
              id={`${name}-skip`}
              checked={isSkipped}
              onCheckedChange={handleSkipChange}
              disabled={disabled}
              className="data-[state=checked]:bg-primary data-[state=checked]:border-primary border-2 rounded-[4px]"
            />
            <label
              htmlFor={`${name}-skip`}
              className="text-[15px] text-gray-700 cursor-pointer select-none"
            >
              {skipLabel}
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchFieldWrapper;
